import { useRef, useState } from "react";
import { useQueryClient, type QueryClient } from "@tanstack/react-query";
import type { ChildProfile } from "../types";
import type { ReadingActor } from "../../shared/child-profile";

type Appearance = Pick<ChildProfile, "colorPalette" | "emoji">;

export function updateChildCaches(client: QueryClient, child: ChildProfile) {
  client.setQueriesData<ChildProfile>({ queryKey: ["play"] }, previous =>
    previous?.id === child.id ? { ...previous, ...child } : previous);
  client.setQueryData<ChildProfile[]>(["children"], previous =>
    previous?.map(item => item.id === child.id ? { ...item, ...child } : item));
  client.setQueryData<{ context?: ReadingActor; status: number }>(["reading-context"], previous =>
    previous?.context?.child?.id === child.id
      ? { ...previous, context: { ...previous.context, child } } : previous);
}

/** The same confirmed save and retry behavior for every child entry point. */
export function useChildAppearance(child: ChildProfile) {
  const client = useQueryClient();
  const busy = useRef(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [failed, setFailed] = useState<Appearance>();
  async function save(updates: Appearance) {
    if (busy.current) return;
    busy.current = true;
    setSaving(true);
    setError("");
    try {
      const response = await fetch("/api/child/profile", {
        method: "PATCH",
        headers: { "Content-Type": "application/json", "X-Child-Id": child.id },
        body: JSON.stringify(updates),
      });
      if (response.status === 409) {
        setFailed(undefined);
        setError("הילד הפעיל השתנה. יש לבחור שוב ילד לפני שינוי ההגדרות.");
        return;
      }
      if (!response.ok) throw new Error("profile_save_failed");
      const saved = await response.json() as ChildProfile;
      if (saved.id !== child.id) throw new Error("profile_identity_mismatch");
      updateChildCaches(client, saved);
      setFailed(undefined);
    } catch {
      setFailed(updates);
      setError("לא הצלחנו לשמור את הבחירה. אפשר לנסות שוב.");
    } finally {
      busy.current = false;
      setSaving(false);
    }
  }
  return { save, saving, error, retry: failed ? () => void save(failed) : undefined };
}
